import type { ChannelKind } from './api';

/**
 * The mark each channel wears (context.md §69).
 *
 * On an omnichannel screen the channel is the first thing an agent reads — a WhatsApp reply and an
 * Instagram DM are answered differently, and a list where they look alike is a list somebody has to
 * read twice. So every conversation carries its channel's mark on the avatar, in the channel's own
 * colour, and the mark is never the only signal: the name of the channel is written beside it.
 *
 * The shapes are drawn, not the brands' own logos. A product does not need somebody else's
 * trademark to say "this arrived on WhatsApp", and shipping a competitor's or a supplier's artwork
 * is a licence question nobody needs.
 */

const TONE: Record<ChannelKind, string> = {
  WHATSAPP: '#25d366',
  INSTAGRAM: '#d62976',
  FACEBOOK: '#0866ff',
  TELEGRAM: '#2aabee',
  SMS: '#f59e0b',
  EMAIL: '#64748b',
  WEBCHAT: '#6366f1',
};

export function channelColor(kind: ChannelKind | string): string {
  return TONE[kind as ChannelKind] ?? 'var(--color-muted)';
}

function Glyph({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="channel-icon">
      {children}
    </svg>
  );
}

/** A speech bubble with a tail, which is what everybody draws for a messaging app. */
function ChatGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M12 3.5a8.5 8.5 0 0 0-7.4 12.7L3.6 20.4l4.3-1.1A8.5 8.5 0 1 0 12 3.5Z" />
    </Glyph>
  );
}

function CameraGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <rect x="4" y="4" width="16" height="16" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17" cy="7" r="1.2" />
    </Glyph>
  );
}

function SocialGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="M13.5 22v-8h2.6l.4-3h-3V9.2c0-.9.3-1.5 1.6-1.5H16.7V5.1A22 22 0 0 0 14.4 5C12.2 5 10.7 6.3 10.7 8.9V11H8.2v3h2.5v8" />
    </Glyph>
  );
}

function PlaneGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="m7 12.4 9.2-3.6-1.5 8.4-3-2.3-1.6 1.6-.2-2.6z" />
    </Glyph>
  );
}

function SmsGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <rect x="3" y="6" width="18" height="12" rx="3" />
      <path d="M7 11h10M7 14h6" />
    </Glyph>
  );
}

function MailGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="m3.5 8 8.5 6 8.5-6" />
    </Glyph>
  );
}

function WidgetGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <rect x="3" y="5" width="18" height="13" rx="2" />
      <path d="M3 9h18M7 13h7" />
    </Glyph>
  );
}

const GLYPHS: Record<ChannelKind, () => React.JSX.Element> = {
  WHATSAPP: ChatGlyph,
  INSTAGRAM: CameraGlyph,
  FACEBOOK: SocialGlyph,
  TELEGRAM: PlaneGlyph,
  SMS: SmsGlyph,
  EMAIL: MailGlyph,
  WEBCHAT: WidgetGlyph,
};

/** The mark alone, for a filter row or beside a label. */
export function ChannelMark({
  kind,
  size = 'sm',
}: {
  kind: ChannelKind | string;
  size?: 'sm' | 'md';
}): React.JSX.Element {
  const Icon = GLYPHS[kind as ChannelKind] ?? WidgetGlyph;

  return (
    <span
      className={`channel-mark channel-mark--${size}`}
      style={{ '--channel-color': channelColor(kind) } as React.CSSProperties}
    >
      <Icon />
    </span>
  );
}
